import axios from 'axios';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import GlobalSidebarItem from '../components/sidebar/GlobalSidebar/GlobalSidebarItem';
import MentorshipSessions from './components/MentorshipSessions';
import FormulaSheets from './components/FormulaSheets';
import RevisionNotes from './components/RevisionNotes';
import ImportantPdfs from './components/ImportantPdfs';
import SyllabusSchedule from './components/SyllabusSchedule';
import SpecialNotes from './components/SpecialNotes';
import PurchasePackModal from '../components/modals/PurchasePackModal';

import { ArrowLeftIcon, CheckIcon } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { usePackStorage } from '../Layout/usePackStorage';
import { AppLayout } from '../Layout/AppLayout';

export default function Resources() {
  const { purchasedPacks, selectedPack, handlePackChange } = usePackStorage();

  const navigate = useNavigate();
  const { packId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [resources, setResources] = useState(null);
  const [selectedTab, setSelectedTab] = useState(null);
  const [tabDescriptionMapping, setTabDescriptionMapping] = useState({});

  const user = JSON.parse(localStorage.getItem('userProfile'));
  const freePacks = JSON.parse(localStorage.getItem('freePacks'));
  const freePack = freePacks?.find((pack) => pack._id.toString() === packId.toString());

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await axios.get(`/api/packs/${packId}/resources`);
        let resources = res?.data?.data;
        if (resources) {
          resources = resources.filter((item) => !item.isExternalLink);
          const tabDescriptionMapping = {};
          resources.forEach((item) => {
            tabDescriptionMapping[item.title] = item.description;
          });
          resources.push({
            title: 'Syllabus & Schedule',
            description: 'Syllabus & Schedule for the Test Series Batch',
            icon: 'NoteBlankIcon',
          });
          tabDescriptionMapping['Syllabus & Schedule'] = 'Syllabus & Schedule for the Test Series Batch';
          setTabDescriptionMapping(tabDescriptionMapping);
          setResources(resources);

          const resourceTitleFromURL = searchParams.get('tab');
          const matchedResource = resources.find((item) => item.title === resourceTitleFromURL);

          if (matchedResource) {
            setSelectedTab(matchedResource.title);
          } else {
            setSelectedTab(resources?.[0]?.title);
          }
        } else {
          navigate('/v2/home');
        }
      } catch (error) {
        navigate('/v2/home');
      }
    }

    fetchData();
  }, [packId]);

  const handleTabChange = (tab) => {
    setSelectedTab(tab.title);
  };

  useEffect(() => {
    if (selectedTab) {
      let webengageData = {
        'User Id': user?.email,
        'Pack Id': packId,
      };
      if (selectedTab === 'Mentorship Sessions') {
        webengageData['Resource'] = 'Mentorship Sessions';
      } else if (selectedTab === 'Formula Sheets') {
        webengageData['Resource'] = 'Formula Sheets';
      } else if (selectedTab === 'Revision Notes') {
        webengageData['Resource'] = 'Revision Notes';
      } else if (selectedTab === 'Important PDFs') {
        webengageData['Resource'] = 'Important PDFs';
      } else if (selectedTab === 'Syllabus & Schedule') {
        webengageData['Resource'] = 'Syllabus & Schedule';
      } else if (selectedTab === 'Special Notes') {
        webengageData['Resource'] = 'Special Notes';
      }

      window.webengage.track('Viewed Resources', webengageData);
    }
  }, [selectedTab]);

  if (!resources) return null;

  return (
    <AppLayout purchasedPacks={purchasedPacks} selectedPack={selectedPack} onPackChange={handlePackChange}>
      <div className="flex flex-row max-w-7xl mx-auto flex-1 m-0 p-0 overflow-hidden">
        {/* Left Sidebar */}
        <div className="hidden lg:flex flex-col w-[264px] items-start pl-6">
          <div className="text-[var(--q3-neutral-default)] text-2xl font-bold my-2 px-4 py-6">Resources</div>
          <div className="w-full overflow-y-auto pr-2">
            {resources.map((tab, index) => (
              <GlobalSidebarItem
                key={index}
                icon={tab.icon}
                title={tab.title}
                isSelected={tab.title === selectedTab}
                onClick={() => handleTabChange(tab)}
                className="w-full"
              />
            ))}
          </div>
        </div>
        <div className="lg:hidden q3-glass fixed border-t bottom-0 z-20 bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)] w-full py-3 px-2">
          <div className="flex flex-row gap-[11px] overflow-x-auto">
            {resources?.map((tab) => {
              return (
                <Button
                  variant={'outline'}
                  className={`rounded-full ${selectedTab === tab.title ? '!bg-[var(--q3-neutral-default)] text-[var(--q3-neutral-default_inverted)] hover:!text-[var(--q3-neutral-default_inverted)] hover:!bg-[var(--q3-neutral-default)]' : 'hover:bg-[var(--q3-surface-dimmest)]'}`}
                  onClick={() => handleTabChange(tab)}
                  key={`tab-${tab.title}`}
                >
                  {tab.title}
                  {selectedTab === tab.title && <CheckIcon />}
                </Button>
              );
            })}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex flex-col w-full overflow-y-auto">
          <div className="sticky top-0 z-10 flex flex-col lg:flex-row justify-between p-6 pb-4 q3-glass rounded">
            <div
              className="block lg:hidden bg-[var(--q3-surface-dim)] rounded-full w-fit p-2 mb-2 cursor-pointer"
              onClick={() => navigate('/v2/home')}
            >
              <ArrowLeftIcon size={24} className="text-[var(--q3-neutral-default)]" />
            </div>
            <div className="flex gap-2 flex-col items-start">
              <div>
                <p className="text-2xl text-[var(--q3-neutral-default)] font-bold">{selectedTab}</p>
                <p className="text-sm text-[var(--q3-neutral-light)]">{tabDescriptionMapping[selectedTab]}</p>
              </div>
            </div>
          </div>
          {/* Render content for specific tabs */}
          <div className="p-6 pt-4 pb-24 lg:pb-6">
            {!purchasedPacks?.length && !user.paid ? (
              <PurchasePackModal
                couponCode={freePack?.couponCode}
                exam={[freePack?.exam]}
                packTitle={freePack?.title}
                paymentItemId={freePack?.paymentItemId}
                showClose={false}
                isResources={true}
                showSidebar={true}
              />
            ) : selectedTab === 'Mentorship Sessions' ? (
              <MentorshipSessions packId={packId} />
            ) : selectedTab === 'Formula Sheets' ? (
              <FormulaSheets packId={packId} selectedPack={selectedPack} />
            ) : selectedTab === 'Revision Notes' ? (
              <RevisionNotes packId={packId} selectedPack={selectedPack} />
            ) : selectedTab === 'Important PDFs' ? (
              <ImportantPdfs packId={packId} />
            ) : selectedTab === 'Syllabus & Schedule' ? (
              <SyllabusSchedule packId={packId} />
            ) : selectedTab === 'Special Notes' ? (
              <SpecialNotes packId={packId} />
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
